import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { RotateCcw, Trophy, Pause, Play } from 'lucide-react';

interface Position {
  x: number;
  y: number;
}

interface SnakeProps {
  onClose?: () => void;
}

export const Snake: React.FC<SnakeProps> = ({ onClose }) => {
  const [snake, setSnake] = useState<Position[]>([{ x: 10, y: 10 }]);
  const [food, setFood] = useState<Position>({ x: 15, y: 15 });
  const [direction, setDirection] = useState<Position>({ x: 0, y: -1 });
  const [gameRunning, setGameRunning] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(() => {
    return parseInt(localStorage.getItem('snakeHighScore') || '0'); // Keep for game state
  });
  const [isPaused, setIsPaused] = useState(false);

  const BOARD_WIDTH = 20;
  const BOARD_HEIGHT = 20;
  const GAME_SPEED = 150;

  const generateFood = useCallback(() => {
    let newFood: Position;
    do {
      newFood = {
        x: Math.floor(Math.random() * BOARD_WIDTH),
        y: Math.floor(Math.random() * BOARD_HEIGHT)
      };
    } while (snake.some(segment => segment.x === newFood.x && segment.y === newFood.y));
    return newFood;
  }, [snake]);

  const resetGame = () => {
    setSnake([{ x: 10, y: 10 }]);
    setFood({ x: 15, y: 15 });
    setDirection({ x: 0, y: -1 });
    setGameRunning(false);
    setGameOver(false);
    setScore(0);
    setIsPaused(false);
  };

  const startGame = () => {
    if (gameOver) {
      resetGame();
    }
    setGameRunning(true);
    setIsPaused(false);
  };

  const pauseGame = () => {
    setIsPaused(!isPaused);
  };

  const moveSnake = useCallback(() => {
    if (!gameRunning || gameOver || isPaused) return;

    setSnake(currentSnake => {
      const newSnake = [...currentSnake];
      const head = { ...newSnake[0] };
      
      head.x += direction.x;
      head.y += direction.y;

      // Check wall collisions
      if (head.x < 0 || head.x >= BOARD_WIDTH || head.y < 0 || head.y >= BOARD_HEIGHT) {
        setGameOver(true);
        setGameRunning(false);
        return currentSnake;
      }

      // Check self collision
      if (newSnake.some(segment => segment.x === head.x && segment.y === head.y)) {
        setGameOver(true);
        setGameRunning(false);
        return currentSnake;
      }

      newSnake.unshift(head);

      // Check food collision
      if (head.x === food.x && head.y === food.y) {
        setScore(prev => {
          const newScore = prev + 10;
          if (newScore > highScore) {
            setHighScore(newScore);
            localStorage.setItem('snakeHighScore', newScore.toString()); // Keep for game state
          }
          return newScore;
        });
        setFood(generateFood());
      } else {
        newSnake.pop();
      }

      return newSnake;
    });
  }, [direction, food, gameRunning, gameOver, isPaused, highScore, generateFood]);

  useEffect(() => {
    const gameInterval = setInterval(moveSnake, GAME_SPEED);
    return () => clearInterval(gameInterval);
  }, [moveSnake]);

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (!gameRunning || isPaused) return;

      switch (e.key) {
        case 'ArrowUp':
          if (direction.y !== 1) setDirection({ x: 0, y: -1 });
          break;
        case 'ArrowDown':
          if (direction.y !== -1) setDirection({ x: 0, y: 1 });
          break;
        case 'ArrowLeft':
          if (direction.x !== 1) setDirection({ x: -1, y: 0 });
          break;
        case 'ArrowRight':
          if (direction.x !== -1) setDirection({ x: 1, y: 0 });
          break;
        case ' ':
          e.preventDefault();
          pauseGame();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [direction, gameRunning, isPaused]);

  const handleDirectionClick = (newDirection: Position) => {
    if (!gameRunning || isPaused) return;
    
    // Prevent moving into self
    if ((direction.x !== 0 && newDirection.x !== 0) || (direction.y !== 0 && newDirection.y !== 0)) return;
    if (direction.x === -newDirection.x || direction.y === -newDirection.y) return;
    
    setDirection(newDirection);
  };

  return (
    <div className="w-full h-full bg-gradient-to-br from-green-800 to-green-900 p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 bg-black/30 rounded-lg p-3">
        <div className="flex items-center gap-4">
          <h2 className="text-white font-bold text-lg">Snake Game</h2>
          <div className="text-white text-sm">
            Score: {score} | High: {highScore}
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={resetGame}
            className="bg-white/10 text-white border-white/20 hover:bg-white/20"
          >
            <RotateCcw size={16} className="mr-1" />
            Reset
          </Button>
          {onClose && (
            <Button
              variant="outline"
              size="sm"
              onClick={onClose}
              className="bg-white/10 text-white border-white/20 hover:bg-white/20"
            >
              Close
            </Button>
          )}
        </div>
      </div>

      {/* Game Area */}
      <div className="flex flex-col items-center">
        {/* Game Board */}
        <div className="relative bg-black border-4 border-gray-600 rounded-lg mb-4">
          <div 
            className="grid gap-0"
            style={{ 
              gridTemplateColumns: `repeat(${BOARD_WIDTH}, 1fr)`,
              width: '400px',
              height: '400px'
            }}
          >
            {Array.from({ length: BOARD_HEIGHT }).map((_, row) =>
              Array.from({ length: BOARD_WIDTH }).map((_, col) => {
                const isSnake = snake.some(segment => segment.x === col && segment.y === row);
                const isHead = snake[0]?.x === col && snake[0]?.y === row;
                const isFood = food.x === col && food.y === row;
                
                return (
                  <div
                    key={`${row}-${col}`}
                    className={`
                      border-[0.5px] border-gray-800
                      ${isFood ? 'bg-red-500' : ''}
                      ${isSnake ? (isHead ? 'bg-green-300' : 'bg-green-500') : ''}
                    `}
                  />
                );
              })
            )}
          </div>

          {/* Game Over Overlay */}
          {gameOver && (
            <div className="absolute inset-0 bg-black/80 flex items-center justify-center rounded-lg">
              <div className="text-center text-white">
                <h3 className="text-2xl font-bold mb-2">Game Over!</h3>
                <p className="mb-4">Final Score: {score}</p>
                <Button onClick={startGame} className="bg-green-600 hover:bg-green-700">
                  <Trophy size={16} className="mr-2" />
                  Play Again
                </Button>
              </div>
            </div>
          )}

          {/* Pause Overlay */}
          {isPaused && gameRunning && (
            <div className="absolute inset-0 bg-black/60 flex items-center justify-center rounded-lg">
              <div className="text-center text-white">
                <Pause className="w-16 h-16 mx-auto mb-4" />
                <h3 className="text-xl font-bold">Paused</h3>
              </div>
            </div>
          )}
        </div>

        {/* Game Controls */}
        <div className="flex flex-col items-center gap-4">
          {!gameRunning ? (
            <Button 
              onClick={startGame}
              className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 text-lg"
            >
              <Play size={20} className="mr-2" />
              {gameOver ? 'Play Again' : 'Start Game'}
            </Button>
          ) : (
            <Button 
              onClick={pauseGame}
              className="bg-yellow-600 hover:bg-yellow-700 text-white px-8 py-3"
            >
              {isPaused ? <Play size={16} className="mr-2" /> : <Pause size={16} className="mr-2" />}
              {isPaused ? 'Resume' : 'Pause'}
            </Button>
          )}

          {/* Mobile Controls */}
          <div className="grid grid-cols-3 gap-2 w-32">
            <div></div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleDirectionClick({ x: 0, y: -1 })}
              className="bg-white/10 text-white border-white/20 hover:bg-white/20 h-10"
            >
              ↑
            </Button>
            <div></div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleDirectionClick({ x: -1, y: 0 })}
              className="bg-white/10 text-white border-white/20 hover:bg-white/20 h-10"
            >
              ←
            </Button>
            <div></div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleDirectionClick({ x: 1, y: 0 })}
              className="bg-white/10 text-white border-white/20 hover:bg-white/20 h-10"
            >
              →
            </Button>
            
            <div></div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleDirectionClick({ x: 0, y: 1 })}
              className="bg-white/10 text-white border-white/20 hover:bg-white/20 h-10"
            >
              ↓
            </Button>
            <div></div>
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div className="mt-6 bg-black/30 rounded-lg p-4 text-white">
        <h3 className="font-semibold mb-2">How to Play:</h3>
        <ul className="text-sm space-y-1 text-gray-200">
          <li>• Use arrow keys or buttons to control the snake</li>
          <li>• Eat the red food to grow and increase your score</li>
          <li>• Don't hit the walls or your own tail</li>
          <li>• Press Space to pause during the game</li>
          <li>• Try to beat your high score!</li>
        </ul>
      </div>
    </div>
  );
};